import { buscarMedicamento, getMedicamentosPorCategoria } from 'data/medicamentos';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Form, InputGroup, ListGroup } from 'react-bootstrap';

function SeletorMedicamentosPopover({ disabled, onSelectMedicamento }) {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const categorias = getMedicamentosPorCategoria();

  const medicamentosFiltrados = useMemo(() => {
    if (!searchTerm) return categorias;

    const termoBusca = searchTerm.toLowerCase();

    return categorias
      .map((categoria) => ({
        ...categoria,
        items: categoria.items.filter(
          (item) =>
            item.nome.toLowerCase().includes(termoBusca) ||
            item.nomes_comerciais?.some((comercial) => comercial.toLowerCase().includes(termoBusca))
        ),
      }))
      .filter((categoria) => categoria.items.length > 0);
  }, [searchTerm, categorias]);

  const toggleCategory = (index) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSelectMedicamento = (medicamento) => {
    const medicamentoCompleto = buscarMedicamento(medicamento.nome);
    onSelectMedicamento(medicamentoCompleto);

    // Limpar busca e fechar dropdown após seleção
    setSearchTerm('');
    setShowDropdown(false);
    setExpandedCategories({});
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [showDropdown]);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <Button
        variant="success"
        size="sm"
        disabled={disabled}
        title="Clique para abrir seletor de medicamentos"
        onClick={() => setShowDropdown(!showDropdown)}
        style={{ width: '100%' }}
      >
        + Adicionar Medicamento
      </Button>

      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '0.25rem',
            boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)',
            marginTop: '4px',
            maxWidth: '500px',
          }}
        >
          <InputGroup size="sm" className="mb-2">
            <Form.Control
              placeholder="Buscar por nome ou marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              aria-label="Buscar medicamento"
            />
            {searchTerm && (
              <Button variant="outline-secondary" onClick={() => setSearchTerm('')} size="sm">
                ✕
              </Button>
            )}
          </InputGroup>

          <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
            {medicamentosFiltrados.length > 0 ? (
              medicamentosFiltrados.map((categoria, catIndex) => (
                <div key={categoria.categoria}>
                  <div
                    onClick={() => toggleCategory(catIndex)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #dee2e6',
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: 500,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{categoria.categoria}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge bg="primary" pill>
                        {categoria.items.length}
                      </Badge>
                      <span style={{ fontSize: '16px' }}>
                        {expandedCategories[catIndex] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>

                  {expandedCategories[catIndex] && (
                    <ListGroup variant="flush">
                      {categoria.items.map((item) => (
                        <ListGroup.Item
                          key={item.nome}
                          action
                          onClick={() => handleSelectMedicamento(item)}
                          className="py-2 border-start-0 border-end-0"
                          style={{
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderBottom: '1px solid #e9ecef',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e7f3ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div>
                            <strong className="text-primary">{item.nome}</strong>
                            {item.nomes_comerciais?.length > 0 && (
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                <small>💊 {item.nomes_comerciais.join(', ')}</small>
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: '11px',
                                marginTop: '4px',
                                color: '#555',
                                fontStyle: 'italic',
                              }}
                            >
                              📋 {item.orientacao}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              ))
            ) : (
              <small className="text-center text-muted">
                <p className="text-muted">Nenhum medicamento encontrado</p>
              </small>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SeletorMedicamentosPopover;
